// @ts-strict-ignore
import { Breadcrumbs } from '@components/Breadcrumbs'
import CollapsibleText from '@components/CollapsibleText'
import LocalizedLink from '@components/Localization/LocalizedLink'
import EnFaqMessages from '@components/Localization/messages/faq/en'
import NlFaqMessages from '@components/Localization/messages/faq/nl'
import urlSlugItemsDe from '@components/Localization/messages/urlSlugItems/de'
import urlSlugItemsEn from '@components/Localization/messages/urlSlugItems/en'
import urlSlugItemsNl from '@components/Localization/messages/urlSlugItems/nl'
import { FloatingServiceMenu, Footer, Header, Page, PageTransition } from '@components/Page'
import { pickSeoProps } from '@components/Page/Page'
import ServiceSection from '@components/ServiceSection'
import CategoryLink from '@components/_faq-detail/CategoryLink'
import {
  enToCategoryCodename,
  nlCategorySlugToEn,
  nlToCategoryCodeName,
} from '@components/_faq/questions'
import { Box, Container, Grid, OldHeading, theme } from '@gassan-ui'
import { SingleItemResponse } from '@lib/kontent'
import { FaqPage as FaqPageModel } from '@lib/kontent/models/FaqPage'
import { useTranslation } from 'next-i18next'
import { useRouter } from 'next/router'
import { FC, useEffect, useState } from 'react'

type FaqDetailPageProps = SingleItemResponse<FaqPageModel> & {
  slug: string
}

let timer

export const FaqDetailPage: FC<FaqDetailPageProps> = ({ slug, item, modular_content }) => {
  const {
    t,
    i18n: { language },
  } = useTranslation(['faq', 'other'])
  const { query } = useRouter()
  const [selectedQuestion, setSelectedQuestion] = useState<string | null>(
    query.id ? query.id.toString() : null,
  )

  const pageTitle =
    language === 'nl'
      ? t(`${nlCategorySlugToEn[slug]}.title`, { ns: 'faq' })
      : t(`${slug}.title`, { ns: 'faq' })
  const toggleQuestion = (urlSlug: string) => {
    setSelectedQuestion(selectedQuestion === urlSlug ? null : urlSlug)
  }
  const codename = language === 'nl' ? nlToCategoryCodeName[slug] : enToCategoryCodename[slug]
  const questions = item.elements[codename]
    ? item.elements[codename].value.map((key) => modular_content[key])
    : []

  useEffect(() => {
    clearTimeout(timer)
    if (selectedQuestion && typeof window !== 'undefined') {
      const selectedEl = document.getElementById(selectedQuestion)
      timer = window.setTimeout(() => {
        if (selectedEl) {
          const offsetToViewport = selectedEl.getBoundingClientRect().top
          const scrollAmount = window.scrollY
          if (offsetToViewport + scrollAmount > window.innerHeight * 0.8) {
            window.scroll({
              top: offsetToViewport - 50 + scrollAmount,
              left: 0,
              behavior: 'smooth',
            })
          }
        }
      }, 300)
    }
    return () => {
      clearTimeout(timer)
    }
  }, []) // eslint-disable-line

  const nlSlug = language === 'nl' ? slug : NlFaqMessages[slug]?.urlSlug
  const otherSlug =
    language === 'nl'
      ? EnFaqMessages[nlCategorySlugToEn[slug]]?.urlSlug
      : EnFaqMessages[slug]?.urlSlug

  // : EnFaqMessages[nlCategorySlugToEn[slug]]?.urlSlug
  return (
    <Page
      {...pickSeoProps(item.elements)}
      languageAlternates={[
        {
          href: `/nl/${urlSlugItemsNl.faqPage}/${nlSlug}`,
          hrefLang: 'nl',
        },
        {
          href: `/en/${urlSlugItemsEn.faqPage}/${otherSlug}`,
          hrefLang: 'en',
        },
        {
          href: `/de/${urlSlugItemsDe.faqPage}/${otherSlug}`,
          hrefLang: 'de',
        },
      ]}
      slug={`${language === 'nl' ? urlSlugItemsNl.faqPage : urlSlugItemsEn.faqPage}/${
        language === 'nl' ? slug : EnFaqMessages[slug]?.urlSlug
      }`}
      altSlug={`${language === 'nl' ? urlSlugItemsEn.faqPage : urlSlugItemsNl.faqPage}/${
        language === 'nl'
          ? EnFaqMessages[nlCategorySlugToEn[slug]]?.urlSlug
          : NlFaqMessages[slug]?.urlSlug
      }`}
    >
      <FloatingServiceMenu />
      <Header initialVariant="absolute" />
      <PageTransition>
        <Container>
          <Box
            maxWidth="35rem"
            pt={{ _: '2rem', small: '0', large: '2rem', xlarge: '4rem' }}
            pb={{ _: '2.5rem', large: '4rem', xlarge: '5rem' }}
          >
            <Box display={{ _: 'none', small: 'block' }} mb={{ _: '3rem', large: '2rem' }}>
              <Breadcrumbs
                items={[
                  {
                    href: `/${item.elements.url_slug.value}`,
                    label: item.elements.title.value,
                  },
                  {
                    href: `/${item.elements.url_slug.value}/[slug]`,
                    label: pageTitle,
                  },
                ]}
              />
            </Box>
            <OldHeading variant="h1" as="h1" mb={[0, '1rem', '1.5rem']}>
              {pageTitle}
            </OldHeading>
          </Box>
        </Container>
        <Container mb={['3.5rem', '7.5rem']}>
          <Grid
            gridTemplateColumns={{ _: '1fr', large: 'auto minmax(auto, 72rem)' }}
            gridColumnGap="2rem"
          >
            <Box>
              <Box
                bg="pampasLight"
                display={{ _: 'none', large: 'block' }}
                maxWidth="27rem"
                p="2.5rem"
              >
                {Object.keys(enToCategoryCodename).map((category, index) => (
                  <Box key={index}>
                    <LocalizedLink
                      href={`/${item.elements.url_slug.value}/${t(`${category}.urlSlug`, {
                        ns: 'faq',
                      })}`}
                    >
                      <a>
                        <CategoryLink
                          isActive={t(`${category}.urlSlug`, { ns: 'faq' }) === slug}
                          title={t(`${category}.title`, { ns: 'faq' })}
                        />
                      </a>
                    </LocalizedLink>
                  </Box>
                ))}
              </Box>
            </Box>
            <Box borderTop={`1px solid ${theme.colors.shades[300]}`}>
              {questions.map((question) => (
                <CollapsibleText
                  key={question.elements.url_slug.value}
                  id={question.elements.url_slug.value}
                  title={question.elements.title.value}
                  description={question.elements.answer.value}
                  onSelect={() => toggleQuestion(question.elements.url_slug.value)}
                  isActive={selectedQuestion === question.elements.url_slug.value}
                />
              ))}
            </Box>
          </Grid>
        </Container>
        <ServiceSection title={t('cantFigureOut', { ns: 'other' })} />
        <Footer />
      </PageTransition>
    </Page>
  )
}
